import React from 'react';

// Add this interface for the custom input component
export interface CustomInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  label: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
  rows?: number;
  options?: string[];
  className?: string; // Add this line to support className prop
}

// Update the CustomInput component to have a more modern look
const CustomInput: React.FC<CustomInputProps> = ({
  id,
  value,
  onChange,
  label,
  type = 'text',
  required = false,
  placeholder = '',
  rows = 1,
  options,
  className = '' // Add default empty string
}) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    onChange(e.target.value);
  };

  // Add this function to handle touch events specifically
  const handleTouchStart = (e: React.TouchEvent) => {
    // For iOS devices, ensure proper focus behavior without scrolling
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (!isIOS) return;

    try {
      // Store current scroll position
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;

      // Get the target element and cast it to the correct type
      const target = e.currentTarget as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (!target) return;

      // Focus the element
      target.focus();

      // Restore scroll position immediately
      setTimeout(() => {
        window.scrollTo(scrollX, scrollY);
      }, 0);
    } catch (err) {
      console.error('Error in input touch handler:', err);
    }
  };

  return (
    <div className={`input-container ${className}`}>
      <label htmlFor={id} className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
        <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
        {label} {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {type === 'textarea' ? (
        <textarea
          id={id}
          defaultValue={value}
          onBlur={handleInputChange}
          rows={rows}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 shadow-sm transition-all duration-200 hover:border-gray-300 resize-none text-base bg-white"
          placeholder={placeholder}
          required={required}
          onTouchStart={handleTouchStart}
        />
      ) : type === 'select' && options ? (
        <select
          id={id}
          value={value}
          onChange={handleInputChange}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 shadow-sm transition-all duration-200 hover:border-gray-300 text-base bg-white appearance-none bg-no-repeat bg-right pr-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
            backgroundPosition: 'right 0.5rem center',
            backgroundSize: '1.5em 1.5em'
          }}
          required={required}
          onTouchStart={handleTouchStart}
        >
          <option value="">{placeholder || 'Select an option'}</option>
          {options.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={id}
          type={type}
          defaultValue={value}
          onBlur={handleInputChange}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 shadow-sm transition-all duration-200 hover:border-gray-300 text-base bg-white"
          placeholder={placeholder}
          required={required}
          onTouchStart={handleTouchStart}
        />
      )}
    </div>
  );
};

export default CustomInput; 